import { Grid, makeStyles, Menu, MenuItem } from '@material-ui/core';
import { TreeView } from '@material-ui/lab';
import React, { ChangeEvent, useState } from 'react';
import MinusSquare from '../../icons/minus-square';
import PlusSquare from '../../icons/plus-square';
import { NodeTypeName, TitleTree, User, UserRole } from '../../domain/domain';
import { ApplicationState } from '../../reducers';
import { useDispatch, useSelector } from 'react-redux';
import { canHaveChildNodeOfType, isCanDeleteNode, isCanRepealNode, isEnablePrintOption } from '../../utils';
import { findSubTreeByNodeId, getNodeTypeNameText } from './utils';
import { UiActionType } from '../../reducers/ui.reducer';
import NestedMenuItem from 'material-ui-nested-menu-item';
import TitleTreeItemComponent from './TitleTreeItemComponent';
import { ComponentManagementActionType } from '../../reducers/comp.actions';
import ApproveAllNodeDialog from './ApproveAllNodeDialog';
import RejectAllDialog from './RejectAllDialog';

type Props = {
    root: TitleTree,
    expandedNodes: number[],
    selectedNodeId: number | undefined,
    onNodeClicked: (node: TitleTree) => void,
    onNodeToggle: (nodeIds: number[]) => void,
    onApproveSubTreeClicked: (approvedSubTree: TitleTree) => void,
    onRejectSubTreeClicked: (rejectSubTree: TitleTree) => void
}

const useStyles = makeStyles(_ => ({
    deleteMenuItem: {
        color: 'red'
    },
    repealMenuItem: {
        color: 'red',
    },
    printMenuItem: {

    },
    root: {
        overflowY: 'auto',
        height: '90vh',
        maxHeight: '90vh',
        display: 'block'
    }
}));


const TitleTreeView: React.FC<Props> = ({ root, expandedNodes, selectedNodeId, onNodeClicked, onNodeToggle, onApproveSubTreeClicked, onRejectSubTreeClicked }) => {

    let currentUser: User | undefined = useSelector((app: ApplicationState) => app.user.currentUser);

    const dispatch = useDispatch();
    const [contextMenuOpen, setContextMenuOpen] = useState<boolean>(false);
    const [posX, setPosX] = useState<number | undefined>(undefined);
    const [posY, setPosY] = useState<number | undefined>(undefined);
    const [contextMenuSubTree, setContextMenuSubTree] = useState<TitleTree | undefined>(undefined);

    const classes = useStyles();

    const findNearestCitedAncestor = (node: TitleTree | undefined): TitleTree => {
        if (!node?.parentId) return root;

        const parentNode = findSubTreeByNodeId(root, node.parentId);

        if (parentNode && parentNode.citation?.trim() !== "") {
            return parentNode;
        }

        return findNearestCitedAncestor(parentNode || root);
    };

    function handleRepealNode() {
        if (contextMenuSubTree === undefined) {
            return;
        }

        setContextMenuOpen(false);
        setContextMenuSubTree(undefined);
        dispatch({ type: ComponentManagementActionType.REPEAL_NODE_DIALOG_OPEN, open: true, subTree: contextMenuSubTree })
    }

    function handlePrintNode() {
        if (!contextMenuSubTree) {
            return;
        }
        setContextMenuOpen(false);
        setContextMenuSubTree(undefined);
        dispatch({ type: UiActionType.PRINTING_OPTIONS_DIALOG_OPEN, open: true, subTree: contextMenuSubTree })
    }

    function handleDeleteNode() {
        if (!contextMenuSubTree) {
            return;
        }

        dispatch({ type: ComponentManagementActionType.REMOVE_NODE_DIALOG_OPEN, open: true, subTree: contextMenuSubTree })
        setContextMenuOpen(false);
        setContextMenuSubTree(undefined);
    }

    function handleEditNode() {
        dispatch({ type: ComponentManagementActionType.ADD_EDIT_NODE_DIALOG_OPEN, open: true, mode: "edit", subTree: contextMenuSubTree })
        setContextMenuOpen(false);
        setContextMenuSubTree(undefined);
    }

    function handleAddChildNode(nodeType: NodeTypeName) {
        if (contextMenuSubTree === undefined) {
            return;
        }
        let ordinal = 0;
        if (contextMenuSubTree.children.length > 0) {
            ordinal = contextMenuSubTree.children[contextMenuSubTree.children.length - 1].ordinal + 1;
        }

        const parentNode = findNearestCitedAncestor(contextMenuSubTree);
        const parentNodeWithCitation = (contextMenuSubTree?.nodeType === NodeTypeName.DIVISION ||
            contextMenuSubTree?.nodeType === NodeTypeName.DIVISION_PART)
            ? root
            : parentNode;

        dispatch({ type: ComponentManagementActionType.ADD_EDIT_NODE_DIALOG_OPEN, open: true, mode: "add", nodeType: nodeType, ordinal: ordinal, subTree: contextMenuSubTree, grandParentNode: parentNodeWithCitation })
        setContextMenuOpen(false);
        setContextMenuSubTree(undefined);
    }

    function handleAddSiblingNode(ordinal: number, nodeType: NodeTypeName, addDirection: string) {
        const parentNode = findSubTreeByNodeId(root, contextMenuSubTree!.parentId);
        const grandParentNode = parentNode ? findNearestCitedAncestor(parentNode) : null;

        const parentNodeWithCitation = (parentNode?.nodeType === NodeTypeName.DIVISION ||
            parentNode?.nodeType === NodeTypeName.DIVISION_PART)
            ? root
            : grandParentNode;

        dispatch({ type: ComponentManagementActionType.ADD_EDIT_NODE_DIALOG_OPEN, open: true, mode: "add", nodeType: nodeType, subTree: parentNode, ordinal: ordinal, direction: addDirection, grandParentNode: parentNodeWithCitation })
        setContextMenuOpen(false);
        setContextMenuSubTree(undefined);
    }

    function handleApproveAll() {
        if (!contextMenuSubTree) {
            return;
        }

        setContextMenuOpen(false);
        dispatch({ type: ComponentManagementActionType.APPROVE_ALL_NODE_DIALOG_OPEN, open: true, subTree: contextMenuSubTree })
    }

    function handleRejectAll() {
        if (contextMenuSubTree === undefined) {
            return;
        }

        dispatch({ type: ComponentManagementActionType.REJECT_ALL_DIALOG_OPEN, open: true, subTree: contextMenuSubTree })
        setContextMenuOpen(false);
    }

    const getSubtreeMenuItems = (selectedSubTree: TitleTree, currentUser: User) => {
        if (!selectedSubTree) {
            return [];
        }
        let ret = [];
        if (currentUser.userRole === UserRole.NON_PUBLICATIONS_TEAM) {
            ret.push(<MenuItem onClick={handleEditNode}>Edit</MenuItem>);

            if (isEnablePrintOption(selectedSubTree.nodeType)) {
                ret.push(<MenuItem className={classes.printMenuItem} onClick={() => handlePrintNode()}>Print</MenuItem>)
            }
        }
        let parentNode = findSubTreeByNodeId(root, selectedSubTree.parentId);

        if (currentUser.userRole === UserRole.ADMINISTRATOR || currentUser.userRole === UserRole.PUBLICATIONS_TEAM) {
            ret.push(<MenuItem onClick={handleEditNode}>Edit</MenuItem>);

            if (isEnablePrintOption(selectedSubTree.nodeType)) {
                ret.push(<MenuItem className={classes.printMenuItem} onClick={() => handlePrintNode()}>Print</MenuItem>)
            }

            let supportedNodeTypes: NodeTypeName[] = [NodeTypeName.ARTICLE,
            NodeTypeName.DIVISION,
            NodeTypeName.SECTION,
            NodeTypeName.SUBSECTION,
            NodeTypeName.PARAGRAPH,
            NodeTypeName.SUBPARAGRAPH,
            NodeTypeName.SUB_SUBPARAGRAPH];

            if (selectedSubTree.children.length === 0 && selectedSubTree.nodeType !== NodeTypeName.SUB_SUBPARAGRAPH) {
                ret.push((
                    <NestedMenuItem placeholder="" label="Add Child" parentMenuOpen={contextMenuOpen}>
                        {supportedNodeTypes.map(nodeTypeName => {
                            if (canHaveChildNodeOfType(selectedSubTree.nodeType, nodeTypeName)) {
                                return <MenuItem key={`child-${nodeTypeName}`} onClick={() => handleAddChildNode(nodeTypeName)}>{getNodeTypeNameText(nodeTypeName)}</MenuItem>
                            }
                            return null;
                        })}
                    </NestedMenuItem>
                ));
            }

            if (parentNode && contextMenuSubTree?.nodeType !== NodeTypeName.TITLE) {
                for (let i = 0; i < supportedNodeTypes.length; i++) {
                    let nodeType = supportedNodeTypes[i];
                    if (canHaveChildNodeOfType(parentNode.nodeType, nodeType, contextMenuSubTree?.nodeType)) {
                        ret.push(<MenuItem onClick={() => handleAddSiblingNode(selectedSubTree.ordinal, nodeType, "Before")}>Add {getNodeTypeNameText(nodeType)} Before</MenuItem>)
                        ret.push(<MenuItem onClick={() => handleAddSiblingNode(selectedSubTree.ordinal + 1, nodeType, "After")}>Add {getNodeTypeNameText(nodeType)} After</MenuItem>)
                    }
                }
            }

            if (isCanRepealNode(selectedSubTree.nodeType) && !contextMenuSubTree?.subTreeHasUnapprovedChanges) {
                ret.push(<MenuItem className={classes.repealMenuItem} onClick={() => handleRepealNode()}>Repeal</MenuItem>)
            }

            if (isCanDeleteNode(selectedSubTree.nodeType)) {
                ret.push(<MenuItem className={classes.deleteMenuItem} onClick={() => handleDeleteNode()}>Remove Provision</MenuItem>)
            }

            if (currentUser.userRole !== UserRole.PUBLICATIONS_TEAM) {
                ret.push(<MenuItem disabled={!selectedSubTree.subTreeHasUnapprovedChanges} onClick={handleApproveAll}>Approve All</MenuItem>);

                ret.push(<MenuItem onClick={handleRejectAll}>Reject All</MenuItem>);
            }

        }

        if (ret.length === 0) {
            ret.push(<MenuItem>No Actions Available On This Node</MenuItem>)
        }

        return ret;
    }

    const onShowContextMenu = (event: React.MouseEvent, node: TitleTree) => {
        if (!currentUser) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        setContextMenuSubTree(node);
        setPosX(event.clientX - 2);
        setPosY(event.clientY - 4);
        setContextMenuOpen(true);
    }

    let selectedNodes: string[] = [];
    if (selectedNodeId) {
        selectedNodes.push(selectedNodeId.toString());
    }
    if (contextMenuSubTree !== undefined && contextMenuSubTree.nodeId !== selectedNodeId) {
        selectedNodes.push(contextMenuSubTree.nodeId.toString());
    }

    return (
        <>
            <ApproveAllNodeDialog onConfirm={() => {
                if (!contextMenuSubTree) { return; }
                onApproveSubTreeClicked(contextMenuSubTree)
                setContextMenuSubTree(undefined);
            }} />
            <RejectAllDialog onConfirm={() => {
                if (!contextMenuSubTree) { return; }
                onRejectSubTreeClicked(contextMenuSubTree)
                setContextMenuSubTree(undefined);
            }} />
            {currentUser &&
                <Menu
                    keepMounted
                    open={contextMenuOpen}
                    onClose={() => {
                        setContextMenuOpen(false);
                        setContextMenuSubTree(undefined);
                    }}
                    anchorReference="anchorPosition"
                    anchorPosition={
                        posY !== null && posX !== null
                            ? { top: posY!, left: posX! }
                            : undefined
                    }>
                    {contextMenuSubTree && getSubtreeMenuItems(contextMenuSubTree, currentUser)}
                </Menu>
            }
            <Grid container direction='column' className={classes.root} spacing={1}>
                <Grid item>
                    <TreeView
                        defaultExpanded={["" + root.nodeId]}
                        expanded={expandedNodes.map(nodeId => "" + nodeId)}
                        selected={selectedNodes}
                        onNodeToggle={(_: ChangeEvent<{}>, nodeIds: string[]) => {
                            let changedNodes = nodeIds.map(nodeId => parseInt(nodeId));
                            onNodeToggle(changedNodes);
                        }}
                        defaultCollapseIcon={<MinusSquare />}
                        defaultExpandIcon={<PlusSquare />}
                    >
                        <TitleTreeItemComponent parent={root} itemRoot={root} onNodeClicked={onNodeClicked} onContextMenu={onShowContextMenu} />
                    </TreeView>
                </Grid>
            </Grid>
        </>
    )

}

export default TitleTreeView;